import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule, Provider } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { Router, RouteReuseStrategy } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';
import { DatePipe, registerLocaleData } from '@angular/common';
import localeRu from '@angular/common/locales/ru';
import localePl from '@angular/common/locales/pl';
import localeTh from '@angular/common/locales/th';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { Socket, SocketIoModule } from 'ngx-socket-io';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { AuthInterceptor } from '@interceptors/auth.interceptor';
import { environment } from '@environments/environment';
import { AuthService } from '@services/auth.service';
import { Client } from '@api/client';
import { ExtendedClient } from '@api/extended-client';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { WebSocketController } from '@services/web-socket.controller';
import { TraceService } from '@sentry/angular';
import { MissingTranslationHandler, TranslateCompiler, TranslateModule, TranslateModuleConfig, TranslateService } from '@ngx-translate/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import type { IonicConfig } from '@ionic/core';
import { CombinedErrorHandlerService } from '@services/error-handlers/combined-error-handler.service';
import { DownloadAppPromptComponent } from '@ui-components/download-app-prompt/download-app-prompt.component';
import { MyMissingTranslationHandler, TranslateLoaderProvider } from '@utils/translate-provider';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { OfflineToastComponent } from '@ui-components/offline-toast/offline-toast.component';
import { ConfigService } from '@services/config.service';
import { Diagnostic } from '@awesome-cordova-plugins/diagnostic/ngx';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SplashScreen } from '@capacitor/splash-screen';
import { PlatformService } from '@services/platform.service';
import { StatusBarService } from '@services/status-bar.service';
import { firstValueFrom } from 'rxjs';
import { UserAvatarComponent } from '@ui-components/user-avatar/user-avatar.component';
import { ActiveAudioCallWidgetComponent } from '@call/ui-components/active-audio-call-widget/active-audio-call-widget.component';
import { StateOverlayService, StateRouterService } from '@state-routing/public-api';

registerLocaleData(localeRu);
registerLocaleData(localePl);
registerLocaleData(localeTh);

const SentryAndTelegramErrorHandlerProviders: Provider = {
  provide: ErrorHandler,
  useClass: CombinedErrorHandlerService
};

const SentryTraceService: Provider = {
  provide: TraceService,
  deps: [Router],
};

const SentryAppInitializer: Provider = {
  provide: APP_INITIALIZER,
  useFactory: () => (): void => {
  },
  deps: [TraceService],
  multi: true,
};

const RouteStrategyProvider: Provider = {
  provide: RouteReuseStrategy,
  useClass: IonicRouteStrategy
};

const LocaleIdProvider: Provider = {
  provide: LOCALE_ID,
  deps: [TranslateService],
  useFactory: (languageService: TranslateService): string => {
    const langs: string[] = environment.activeLocales;
    const lang: string = languageService.currentLang || languageService.defaultLang;
    return lang
      ? langs.find((l: string) => lang.startsWith(l))
      : 'en-US';
  }
};

const AuthInterceptorProvider: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: AuthInterceptor,
  multi: true
};

const ApiInitializerProvider: Provider = {
  provide: APP_INITIALIZER,
  useFactory: (
    authService: AuthService,
    webSocketController: WebSocketController,
    configService: ConfigService,
    client: Client,
    socket: Socket,
    platformService: PlatformService,
    statusBarService: StatusBarService,
  ) => async (): Promise<void> => {
    configService.initializeConfig();
    console.log('[Init] Applying API base URL...');
    client['baseUrl'] = configService.urlBackEndApi;
    console.log('[Init] API base URL:', client['baseUrl']);
    console.log('[Init] Applying Socket URL...');
    socket['ioSocket'].io.uri = configService.socketConfig.url;
    console.log('[Init] Socket URL:', socket['ioSocket'].io.uri);

    if (platformService.isAndroidApp) {
      statusBarService.setBackgroundColor().catch((error: unknown) => console.error('Status bar init error', error));
    }
    if (platformService.isDevice) {
      SplashScreen.hide()
        .then(() => console.log('SplashScreen is hidden!'))
        .catch((error) => console.log('SplashScreen is not hidden!', error));
    }

    await authService.init();

    if (!platformService.isSSR) {
      await webSocketController.init();
    }
  },
  deps: [
    AuthService,
    WebSocketController,
    ConfigService,
    Client,
    Socket,
    PlatformService,
    StatusBarService,
  ],
  multi: true
};

const TranslationInitializer: Provider = {
  provide: APP_INITIALIZER,
  useFactory: (translate: TranslateService) => {
    return () => {
        const defaultLang = environment.defaultLanguage
        translate.setDefaultLang(defaultLang);
        return firstValueFrom(translate.use(defaultLang));
    };
  },
  deps: [TranslateService],
  multi: true,
};

const translateModuleConfig: TranslateModuleConfig = {
  loader: TranslateLoaderProvider,
  missingTranslationHandler: {provide: MissingTranslationHandler, useClass: MyMissingTranslationHandler},
  useDefaultLang: true,
  compiler: {
    provide: TranslateCompiler,
    useClass: TranslateMessageFormatCompiler
  }
};

//const socketModuleConfig: SocketIoConfig = environment.socketConfig;

const SocketProvider: Provider = {
  provide: Socket,
  useFactory: (config: ConfigService) => new Socket(config.socketConfig),
  deps: [ConfigService]
};

const ionicModuleConfig: IonicConfig = {
  swipeBackEnabled: false,
  loadingSpinner: 'crescent',
  backButtonDefaultHref: '/avatar',
};

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    BrowserModule.withServerTransition({appId: 'serverApp'}),
    IonicModule.forRoot(ionicModuleConfig),
    SocketIoModule,
    AppRoutingModule,
    InfiniteScrollModule,
    HttpClientModule,
    TranslateModule.forRoot(translateModuleConfig),
    DownloadAppPromptComponent,
    OfflineToastComponent,
    UserAvatarComponent,
    ActiveAudioCallWidgetComponent,
    BrowserAnimationsModule,
  ],
  providers: [
    TranslationInitializer, // required for tag translations in SSR mode!
    SentryAppInitializer,
    SentryTraceService,
    SentryAndTelegramErrorHandlerProviders,
    RouteStrategyProvider,
    LocaleIdProvider,
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideMessaging(() => getMessaging()),
    Client,
    ExtendedClient,
    SocketProvider,
    AuthInterceptorProvider,
    ApiInitializerProvider,
    StateOverlayService,
    StateRouterService,
    Diagnostic,
    DatePipe
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
}

//#region fix of FileReader issue during capacitor build
// https://github.com/ionic-team/capacitor/issues/1564

if (typeof window !== 'undefined') {
  class FileReaderA extends window.FileReader {
    constructor() {
      super();
      const zoneOriginalInstance = (this as any)['__zone_symbol__originalInstance'];
      return zoneOriginalInstance || this;
    }
  }

  window.FileReader = FileReaderA;
}
//#endregion fix of FileReader issue during capacitor build
